<script setup lang="ts">
import { Mitteilung, MODE } from '@/models/Mitteilung'
import { Gesuch, GesuchStatus } from '@/models/Gesuch'
import Flow, { type FlowFile } from '@flowjs/flow.js'
import KpTextfield from '@/components/kp-textfield.vue'
import { watch, onBeforeUnmount, computed, onMounted, ref, useTemplateRef, type Ref } from 'vue'
import { formRules } from '@/helpers/validators'
import { buildUrl } from '@/helpers/api'
import { getCsrfToken } from '@/helpers/auth'
import { createModel } from '@kp/js-model'
import { useToast } from '@/composables/toast'
import { useI18n } from 'vue-i18n'
import { errToStr } from '@/helpers/tools'
import type { ValidationResult } from 'vuetify/lib/composables/validation.mjs'
import KpTextarea from '../kp-textarea.vue'
import KpFieldlabel from '../kp-fieldlabel.vue'

const modelValue = defineModel<boolean>('modelValue')

const { gesuch, empfaengerlist = [] } = defineProps<{
    gesuch: Gesuch | null
    empfaengerlist: string[]
}>()

const filedrop = useTemplateRef('filedrop')
const recipientSelect = useTemplateRef('recipientSelect')
const empfaenger: Ref<string[] | null> = ref(null)
const subject: Ref<string | null> = ref(null)
const mitteilung: Ref<string | null> = ref(null)
const valid = ref(false)
const commentcheckbox = ref(false)
const attachments: Ref<FlowFile[]> = ref([])
const uploading = ref(false)
const rules = formRules
const flow: Ref<Flow | null> = ref(null)

const showCommentCheckbox = computed(() => {
    return (
        gesuch?.status === GesuchStatus.NEW ||
        gesuch?.status === GesuchStatus.READY_FOR_SUBMIT ||
        gesuch?.status === GesuchStatus.SUBMIT
    )
})

const emit = defineEmits<{
    (e: 'close'): void
    (e: 'mitteilungSent'): void
}>()

onMounted(() => {
    flow.value = new Flow({
        singleFile: false,
        simultaneousUploads: 1,
        target: buildUrl('/mitteilung/uploadAttachment'),
        testChunks: false,
        chunkSize: 5 * 1024 * 1024,
        headers: {
            'X-CSRF-Token': getCsrfToken(),
        },
    })
    flow.value.on('fileAdded', onFlowFileAdded.bind(this))
    flow.value.on('fileRemoved', removeFile.bind(this))

    if (filedrop.value) {
        flow.value.assignBrowse([filedrop.value])
        flow.value.assignDrop(filedrop.value)
    }
})

onBeforeUnmount(() => {
    flow.value?.off()
})

function cancel() {
    emit('close')
}

const toast = useToast()
const { t } = useI18n()

async function send() {
    uploading.value = true
    const msg = createModel(Mitteilung, {
        gesuch_id: gesuch?.id,
        empfaenger_rollen: commentcheckbox.value ? 'gesuchcomment' : empfaenger.value,
        mode: commentcheckbox.value ? MODE.BROADCAST : MODE.STANDARD,
        subject: subject.value,
        mitteilung: mitteilung.value,
    })
    try {
        await msg.save()
        if (!msg.id) {
            toast.add({ text: t('fehler_beim_speichern'), color: 'error' })
        }
        await sendAttachments(msg)
        emit('mitteilungSent')
    } catch (res) {
        console.error(res)
        toast.add({ text: errToStr(res), color: 'error' })
    }

    uploading.value = false
}

function empfaengerRules(value: string[] | null) {
    let ret: ValidationResult = false
    if (commentcheckbox.value) {
        return true
    }
    if (value) {
        ret = value.length > 0 ? true : t('empfaneger_missing')
    } else {
        ret = t('empfaneger_missing')
    }
    return ret
}

function addHover(e: DragEvent) {
    if (e.target instanceof HTMLElement) {
        e.target.classList.add('hover')
    }
}

function removeHover(e: DragEvent) {
    if (e.target instanceof HTMLElement) {
        e.target.classList.remove('hover')
    }
}

function onFlowFileAdded(file: FlowFile) {
    attachments.value.push(file)
}

function onFileItemRemove(file: FlowFile) {
    flow.value?.removeFile(file)
}

function removeFile(file: FlowFile) {
    for (let i = attachments.value.length - 1; i >= 0; i--) {
        if (attachments.value[i] === file) {
            attachments.value.splice(i, 1)
        }
    }
}

function sendAttachments(msg: Mitteilung) {
    if (attachments.value.length === 0) {
        return
    }
    return new Promise((resolve, reject) => {
        disableFileInput()
        uploading.value = true
        if (flow.value) {
            flow.value.opts.query = {
                mitteilung_id: msg.id,
            }
        }

        const fn = () => {
            flow.value?.off('complete', fn)
            resolve(true)
        }

        flow.value?.on('complete', fn)

        flow.value?.on('error', (message) => {
            flow.value?.cancel()
            flow.value?.off('complete', fn)
            reject(message)
        })

        flow.value?.upload()
    })
}

function disableFileInput() {
    if (filedrop.value) {
        filedrop.value.getElementsByTagName('input')[0].disabled = true
    }
}

watch(commentcheckbox, (value: boolean) => {
    if (value) {
        recipientSelect.value?.validate()
    }
})
</script>

<template>
    <v-dialog
        :model-value="modelValue"
        max-width="1000"
        @click:outside="$emit('update:modelValue', false)"
    >
        <v-card class="pt-4 pl-4">
            <v-card-title class="ml-2">{{ t('new_message') }}</v-card-title>
            <v-card-text>
                <v-form v-model="valid">
                    <v-checkbox
                        v-if="showCommentCheckbox"
                        v-model:model-value="commentcheckbox"
                        :label="t('gesuch_comment')"
                        style="margin-left: -0.5rem"
                    >
                    </v-checkbox>
                    <div class="mb-4">
                        <KpFieldlabel :label="t('empfaenger')" :disabled="commentcheckbox" />
                        <v-select
                            v-if="empfaengerlist"
                            ref="recipientSelect"
                            v-model:model-value="empfaenger"
                            class="inputfield"
                            :disabled="commentcheckbox"
                            :items="empfaengerlist"
                            :rules="[commentcheckbox || empfaengerRules]"
                            validate-on="lazy"
                            multiple
                        >
                        </v-select>
                    </div>
                    <kp-textfield
                        v-model:model-value="subject"
                        :label="t('betreff')"
                        :rules="[rules.required]"
                        class="mb-4"
                    ></kp-textfield>
                    <KpTextarea
                        v-model:model-value="mitteilung"
                        :label="t('mitteilung')"
                        :rules="[rules.required]"
                    >
                    </KpTextarea>
                    <div
                        ref="filedrop"
                        class="filedrop"
                        @dragover="addHover"
                        @dragleave="removeHover"
                        @drop="removeHover"
                    >
                        <span>{{ t('dateien_anhaengen') }}</span>
                    </div>
                    <div v-for="attachment in attachments" :key="attachment.name" class="fileItem">
                        {{ attachment.name
                        }}<v-btn
                            variant="text"
                            icon="fas fa-trash"
                            color="stop white--text"
                            :disabled="uploading"
                            @click="onFileItemRemove(attachment)"
                        >
                        </v-btn>
                    </div>
                </v-form>
            </v-card-text>
            <v-card-actions>
                <v-spacer></v-spacer>
                <v-btn text color="decline" @click="cancel">
                    {{ t('cancel') }}
                </v-btn>
                <v-btn
                    color="action"
                    :disabled="!valid || uploading"
                    @click="send"
                    prepend-icon="fas fa-paper-plane"
                    variant="flat"
                >
                    {{ t('send') }}
                </v-btn>
            </v-card-actions>
        </v-card>
    </v-dialog>
</template>

<style lang="scss" scoped>
.filedrop {
    margin-top: 2em;
    margin-right: 1em;
    padding: 3em;
    border: 5px dashed lightgray;
    border-radius: 10px;
    color: gray;
    text-align: center;
    vertical-align: middle;

    &.hover {
        border-color: seagreen;
        color: seagreen;
    }
}
.inputfield {
    margin-left: -1rem;
}
.icon-margin {
    margin-left: 0.25rem;
}
</style>
